import React, { useContext, useReducer, useState } from 'react'
import {
  Button,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Paragraph,
  TextField,
  useOverlay,
} from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { deepCopy } from '../../../util/Utils'
import { createAsMember, deleteAsMember, getAsMember } from '../../../services/membershipService'
import useNotifications from '../../../hooks/useNotifications'
import { PermissionsContext } from '../../app/PermissionsContext'

export default () => {}

// Account type

export const ACCOUNT_TYPE_MEMBER = 'member'
export const ACCOUNT_TYPE_SPECIAL_MEMBER = 'specialMember'
export const ACCOUNT_TYPE_PUBLIC = 'public'

export function getAccountType(mrn, isSpecialMember) {
  if (mrn) {
    return ACCOUNT_TYPE_MEMBER
  }
  if (isSpecialMember) {
    return ACCOUNT_TYPE_SPECIAL_MEMBER
  }
  return ACCOUNT_TYPE_PUBLIC
}

// View

export function TreatAsMemberButton({ cisId, mrn, treatAsMemberState, treatAsMemberActions }) {
  const { t } = useTranslation()
  const { isAuthorized } = useContext(PermissionsContext)
  const setAsMemberDialogOpener = useOverlay()
  const resetToPublicDialogOpener = useOverlay()
  const reload = () => treatAsMemberActions.loadData(cisId)

  const isMember = Boolean(mrn)
  if (isMember || treatAsMemberState.isSpecialMember == null || !isAuthorized('ManageTreatAsMember')) {
    return null
  }

  if (treatAsMemberState.isSpecialMember) {
    return (
      <>
        <Button emphasis="medium" onClick={resetToPublicDialogOpener.handleClick}>
          {t('treatAsMember.resetToPublic', 'Reset to Public')}
        </Button>
        <ResetToPublicDialog {...resetToPublicDialogOpener} cisId={cisId} onSaved={reload} />
      </>
    )
  }

  return (
    <>
      <Button emphasis="medium" onClick={setAsMemberDialogOpener.handleClick}>
        {t('treatAsMember.setAsMember', 'Set as Member')}
      </Button>
      <SetAsMemberDialog {...setAsMemberDialogOpener} cisId={cisId} onSaved={reload} />
    </>
  )
}

export function SetAsMemberDialog({ cisId, onSaved, ...opener }) {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const [reason, setReason] = useState('')

  const confirmBtnOnClick = async () => {
    try {
      await createAsMember(cisId, reason)
      setReason('')
      opener.close()
      onSaved()
    } catch (err) {
      error(
        t('treatAsMember.errors.settingAsMember', {
          defaultValue: `Error setting account as member: ${err.message || ''}`,
          message: err.message || '',
        }),
        err
      )
    }
  }

  return (
    <DialogOverlay {...opener} title={t('treatAsMember.setAsMember', 'Set as Member')}>
      <Paragraph size="sm">
        {t(
          'treatAsMember.setAsMember.dialog.body',
          'This will treat this non-member account as if it were a member. Please provide a reason.'
        )}
      </Paragraph>
      <TextField label={t('reason', 'Reason')} value={reason} onChange={(event) => setReason(event.target.value)} />
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button emphasis="high" disabled={reason === ''} onClick={confirmBtnOnClick}>
            {t('action.confirm', 'Confirm')}
          </Button>
          <Button emphasis="medium" onClick={opener.close}>
            {t('action.cancel', 'Cancel')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}

export function ResetToPublicDialog({ cisId, onSaved, ...opener }) {
  const { t } = useTranslation()
  const { error } = useNotifications()

  const confirmBtnOnClick = async () => {
    try {
      await deleteAsMember(cisId)
      opener.close()
      onSaved()
    } catch (err) {
      error(
        t('treatAsMember.errors.resettingToPublic', {
          defaultValue: `Error resetting account to public: ${err.message || ''}`,
          message: err.message || '',
        }),
        err
      )
    }
  }

  return (
    <DialogOverlay {...opener} title={t('treatAsMember.resetToPublic', 'Reset to Public')}>
      <Paragraph size="sm">
        {t(
          'treatAsMember.resetToPublic.dialog.body',
          'This will remove special member treatment from this account, resetting it to public.'
        )}
      </Paragraph>
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button emphasis="high" onClick={confirmBtnOnClick}>
            {t('action.confirm', 'Confirm')}
          </Button>
          <Button emphasis="medium" onClick={opener.close}>
            {t('action.cancel', 'Cancel')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}

// Controller

export function updateStateSetAsMemberData(newState, payload) {
  newState.isSpecialMember = payload.isSpecialMember
  newState.reason = payload.reason
  newState.lastUpdaterCisId = payload.lastUpdaterCisId
  newState.lastUpdaterName = payload.lastUpdaterName
}

function reducer(oldState, action) {
  const newState = deepCopy(oldState)
  action.callback(newState, action.payload)
  return newState
}

export function useTreatAsMemberController(isAuthorized) {
  const notifications = useNotifications()

  const initialState = {
    isSpecialMember: null,
    reason: null,
    lastUpdaterCisId: null,
    lastUpdaterName: null,
  }

  const [treatAsMemberState, treatAsMemberDispatch] = useReducer(reducer, initialState)

  const treatAsMemberActions = {
    treatAsMemberDispatch,
    loadData: async (cisId) => {
      return !cisId || !isAuthorized(['ViewTreatAsMember'])
        ? null
        : getAsMember(cisId)
            .then((data) => {
              treatAsMemberActions.treatAsMemberDispatch({
                callback: updateStateSetAsMemberData,
                payload: {
                  isSpecialMember: true,
                  reason: data.reason,
                  lastUpdaterCisId: data.lastUpdaterCisId,
                  lastUpdaterName: data.lastUpdaterName,
                },
              })
              return Promise.resolve()
            })
            .catch((error) => {
              if (error?.response?.status >= 400 && error?.response?.status < 500) {
                treatAsMemberActions.treatAsMemberDispatch({
                  callback: updateStateSetAsMemberData,
                  payload: { isSpecialMember: false, reason: null, lastUpdaterCisId: null, lastUpdaterName: null },
                })
              } else {
                notifications.error(`Error trying to get As Member data. CisID: ${cisId}`)
              }
              return Promise.resolve() // error has been handled so continue
            })
    },
  }

  return { treatAsMemberState, treatAsMemberActions }
}
